// hooks/usePercentageSelection.ts
import { useState, useCallback } from 'react'

interface UsePercentageSelectionProps {
  baseBalance: number
  quoteBalance: number
  lastPrice: number | null
  onAmountChange: (value: string) => void
  onTotalChange: (value: string) => void
  calculateTotal: (amount: string) => string
  calculateAmount: (total: string) => string
}

/**
 * Custom hook for managing percentage-based balance selection
 * Handles mutual exclusivity between amount and total percentage selections
 */
export const usePercentageSelection = ({
  baseBalance,
  quoteBalance,
  lastPrice,
  onAmountChange,
  onTotalChange,
  calculateTotal,
  calculateAmount
}: UsePercentageSelectionProps) => {
  const [selectedAmountPercentage, setSelectedAmountPercentage] = useState<number | null>(null)
  const [selectedTotalPercentage, setSelectedTotalPercentage] = useState<number | null>(null)

  // Handle amount percentage selection
  const handleAmountPercentageClick = useCallback((percent: number) => {
    if (!baseBalance || baseBalance <= 0) return
    
    const newAmount = (baseBalance * percent / 100).toString()
    onAmountChange(newAmount)
    
    // Set amount percentage and clear total percentage (mutual exclusivity)
    setSelectedAmountPercentage(percent)
    setSelectedTotalPercentage(null)
  }, [baseBalance, onAmountChange])

  // Handle total percentage selection
  const handleTotalPercentageClick = useCallback((percent: number) => {
    if (!quoteBalance || quoteBalance <= 0) return
    
    const newTotal = (quoteBalance * percent / 100).toString()
    onTotalChange(newTotal)
    
    // Set total percentage and clear amount percentage (mutual exclusivity)
    setSelectedTotalPercentage(percent)
    setSelectedAmountPercentage(null)
  }, [quoteBalance, onTotalChange])

  // Reset percentage selections when user manually types
  const resetAmountPercentage = useCallback(() => {
    setSelectedAmountPercentage(null)
  }, [])

  const resetTotalPercentage = useCallback(() => {
    setSelectedTotalPercentage(null)
  }, [])

  const resetAllPercentages = useCallback(() => {
    setSelectedAmountPercentage(null)
    setSelectedTotalPercentage(null)
  }, [])

  return {
    selectedAmountPercentage,
    selectedTotalPercentage,
    handleAmountPercentageClick,
    handleTotalPercentageClick,
    resetAmountPercentage,
    resetTotalPercentage,
    resetAllPercentages
  }
}